/**
 * InputAutocomplete is a component that provides an autocomplete input field.
 * It allows users to select multiple options from a dropdown list.
 *
 * Props:
 * - value: The current value of the autocomplete input, which can be a string, array of strings, or array of IOptions.
 * - options: An array of selectable options, each represented as an object with a 'label' and 'value'.
 * - label: A string to display as the input field's label.
 * - required: Boolean to indicate if the input is required.
 * - labelColor: Color of the label text.
 * - helperText: Additional text to provide guidance or context to the user.
 * - error: Boolean to indicate if there's an error in the input.
 * - onChange: Callback function to handle changes to the input value.
 * - placeholder: Placeholder text to display when the input is empty.
 * - multiple: Boolean to allow selection of multiple options.
 * - disableClearable: Boolean to disable the clear button.
 * - showChip: Determines where the selected options are displayed as chips, either "inside" or "outside".
 */
import { Autocomplete, AutocompleteProps, Chip, FormHelperText, Stack, SxProps, TextField, Theme } from "@mui/material"
import Conditional from "../conditional"
import React from "react"
import Label from "../label"
interface Label {
  value: string
  label: string
  [key: string]: any
}

type IInputAutocompleteProps = {
  // value: string | string[] | IOptions[]
  // options: Label[]
  label?: string
  required?: boolean
  labelColor?: any
  helperText?: string | string[]
  error?: boolean
  onChange: (value: IOptions[]) => void
  placeholder?: string
  multiple?: boolean
  showChip?: "inside" | "outside"
  maxWidth?: string
  labelMb?: number
  height?: string
  getOptionLabel?: (option: any) => string
  sx?: SxProps<Theme>
  slotProps?: any
  isOptionValueSame?: boolean
  valueKey?: string
  startAdornment?: React.ReactNode
} & Omit<AutocompleteProps<any, any, any, any>, "renderInput">

const InputAutocomplete = React.forwardRef(
  (
    {
      value = "",
      // options,
      label,
      required,
      helperText,
      error,
      onChange,
      placeholder,
      multiple,
      showChip,
      maxWidth,
      isOptionValueSame,
      height,
      labelMb,
      getOptionLabel,
      getOptionKey,
      valueKey = "value",
      sx,
      startAdornment,
      ...rest
    }: IInputAutocompleteProps,
    ref?: any
  ) => {
    const handleisOptionEqualToValue = (option: any, value: any) => {
      return getOptionKey?.(option) === getOptionKey?.(value)
    }
    return (
      <Stack sx={{ width: "100%", ...(maxWidth && { maxWidth }) }}>
        <Label mb={labelMb} label={label} labelColor={error ? "error" : undefined} required={required} />
        <Autocomplete
          multiple={multiple}
          sx={{ "& .MuiInputBase-root ": { height }, ...sx }}
          size="small"
          // options={options}
          value={(value ?? (multiple ? [] : "")) as any}
          onChange={(_, newValue) => {
            onChange?.(newValue as any)
          }}
          ref={ref}
          filterSelectedOptions
          getOptionKey={getOptionKey}
          getOptionLabel={getOptionLabel}
          {...(showChip === "outside" && { renderTags: () => <></> })}
          isOptionEqualToValue={isOptionValueSame ? handleisOptionEqualToValue : undefined}
          renderInput={params => (
            <TextField
              {...params}
              value={rest?.loading ? "Loading..." : params?.inputProps?.value}
              disabled={rest?.loading}
              autoComplete={"off"}
              placeholder={placeholder}
              variant="outlined"
              error={error}
              slotProps={{
                input: {
                  ...params.InputProps,
                  ...((!showChip && startAdornment) || (showChip === "outside" && startAdornment)
                    ? {
                        startAdornment: startAdornment
                      }
                    : {})
                }
              }}
            />
          )}
          {...rest}
        />

        <Conditional
          condition={Boolean(multiple && showChip === "outside" && typeof value === "object" && value?.length !== 0)}
        >
          <Stack direction={"row"} flexWrap={"wrap"} gap={2} my={2}>
            {value?.length &&
              (value as IOptions[])?.map(val => (
                <Chip
                  label={getOptionLabel?.(val) ?? val.value}
                  onDelete={() =>
                    onChange?.(
                      (value as IOptions[])?.filter(
                        item => item?.value !== val?.value || getOptionKey?.(val) !== getOptionKey?.(item)
                      )
                    )
                  }
                />
              ))}
          </Stack>
        </Conditional>
        {/* </FormControl> */}
        {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : <></>}
      </Stack>
    )
  }
)
export default InputAutocomplete
